import { REACTION_META, type Pun, type Reaction } from '../../shared/schemas';
import { countReaction, reactionBy } from '../lib/puns';

interface ReactionBarProps {
  pun: Pun;
  viewerId: string;
  /** The viewer has already spent today's one crown somewhere else. */
  crownSpent: boolean;
  onReact: (reaction: Reaction) => void;
}

/**
 * The four reaction buttons under a pun.
 *
 * Extracted rather than inlined: `PunFeed` carried its own copy of this markup
 * alongside a `ReactionButtons` component that nothing imported, so the two
 * drifted and only one of them was live (gotcha 9).
 *
 * Tapping your current reaction again clears it, which matches what `setReaction`
 * does on the server.
 */
function ReactionBar({ pun, viewerId, crownSpent, onReact }: ReactionBarProps) {
  const isOwnPun = pun.userId === viewerId;
  const mine = reactionBy(pun, viewerId);

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {REACTION_META.map(({ emoji, name, label }) => {
        const selected = mine === name;
        const count = countReaction(pun, name);
        // You can always take a crown back; you just cannot spend a second one.
        const blocked = isOwnPun || (name === 'crown' && crownSpent && !selected);

        return (
          <button
            key={name}
            onClick={() => onReact(name)}
            disabled={blocked}
            title={
              isOwnPun
                ? 'No reacting to your own pun'
                : blocked
                  ? 'Your crown is already spent today'
                  : label
            }
            /*
             * Nothing behind an emoji is yellow.
             *
             * Three of the four reactions are yellow glyphs, and this bar
             * previously sat them on mustard when selected and on near-white
             * otherwise, inside a card that was itself washed mustard when it
             * was yours. Yellow on yellow and yellow on white both fail, and
             * the whole card read as one colour.
             *
             * Chips are a cool slate tint now, and the selected one is grape.
             * Both are far enough from yellow to give the glyphs an edge.
             */
            className={`press flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm font-bold leading-none tabular-nums ${
              blocked
                ? 'cursor-not-allowed border-ink/30 bg-ink/[0.06] text-ink/45'
                : selected
                  ? 'border-ink bg-grape text-white shadow-pop'
                  : 'border-ink bg-ink/[0.09] text-ink shadow-pop-sm hover:bg-grape/20'
            }`}
          >
            {/*
              Colour emoji ignore `color`, so the only way to signal "you cannot
              press this" on the glyph itself is opacity. Text colour alone left
              a full-brightness emoji next to a faded count.
            */}
            <span aria-hidden="true" className={`text-lg ${blocked ? 'opacity-50' : ''}`}>
              {emoji}
            </span>
            <span className="sr-only">{label}</span>
            {count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default ReactionBar;
